import React from "react";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import type { DateView, TimeView } from "@mui/x-date-pickers";
import type { PopperProps } from "@mui/material/Popper";
import type { TextFieldProps } from "@mui/material/TextField";

/**
 * Canonical date/time pickers for control-panel (MAX-3909 / MAX-4021).
 *
 * This is the ONE place the v3 `@material-ui/pickers` → v6 `@mui/x-date-pickers`
 * prop-to-slot mapping is written. Every call site renders `AppDatePicker` /
 * `AppTimePicker` with a flattened prop surface that mirrors the direct props the
 * old `KeyboardDatePicker` / `KeyboardTimePicker` accepted. No call site constructs
 * `slotProps` by hand (build spec D3).
 *
 * v6 desktop pickers are inline, toolbar-less, and auto-accept by default, so the
 * old `variant="inline"`, `disableToolbar`, and `autoOk` props are dropped here
 * (D4). The input mask is derived from `format` — no `mask` prop is passed (D5).
 */

// The app is moment-centric while AdapterDateFns wants `Date | null`. Normalize at the
// value boundary only: a Moment converts via `.toDate()`, a Date passes through, and
// null / undefined / false all collapse to null (D2).
export const toDateValue = (value: unknown): Date | null => {
	if (!value) {
		return null;
	}
	if (moment.isMoment(value)) {
		return value.toDate();
	}
	if (value instanceof Date) {
		return value;
	}
	return null;
};

type ClassOverrides = Partial<Record<string, string>>;

interface CommonProps {
	// Accept `undefined` too (some sites feed `task?.date.toDate()`); it is coalesced to
	// null before reaching the v6 picker, which only knows Date | null.
	value: Date | null | undefined;
	onChange: (value: Date | null) => void;
	label?: React.ReactNode;
	format?: string;
	placeholder?: string;
	required?: boolean;
	disabled?: boolean;
	error?: boolean;
	helperText?: React.ReactNode;
	fullWidth?: boolean;
	margin?: "none" | "dense" | "normal";
	minDate?: Date;
	maxDate?: Date;
	/** Icon rendered inside the open-picker button (was v3 `keyboardIcon`). */
	openPickerIcon?: React.ReactNode;
	/** aria-label for the open-picker button; defaults per picker kind. */
	openPickerAriaLabel?: string;
	/** Custom input end-adornment (e.g. lead-form clear button, D7). When set it
	 *  replaces the picker's default open button — matching v3 behavior. */
	endAdornment?: React.ReactNode;
	/** `className` for the input (OutlinedInput). */
	inputClassName?: string;
	/** `classes` bag for the input (OutlinedInput). */
	inputClasses?: ClassOverrides;
	/** `className` for the InputLabel. */
	inputLabelClassName?: string;
	/** `classes` bag for the InputLabel. */
	inputLabelClasses?: ClassOverrides;
	/** `className` for the FormHelperText. */
	helperTextClassName?: string;
	/** Popper placement (was v3 `PopoverProps` anchor/transform origins, D8). */
	popperPlacement?: PopperProps["placement"];
}

// Build the single canonical `slotProps.textField` object shared by both pickers.
const buildTextFieldSlotProps = (
	props: CommonProps
): NonNullable<TextFieldProps> => {
	const {
		fullWidth,
		margin,
		required,
		error,
		helperText,
		placeholder,
		endAdornment,
		inputClassName,
		inputClasses,
		inputLabelClassName,
		inputLabelClasses,
		helperTextClassName,
	} = props;

	const inputProps: Record<string, unknown> = {};
	if (inputClassName) {
		inputProps.className = inputClassName;
	}
	if (inputClasses) {
		inputProps.classes = inputClasses;
	}
	// Only override the end-adornment when a site supplies one; otherwise the picker's
	// default open-picker button is left in place (D7 conditional affordance).
	if (endAdornment !== undefined) {
		inputProps.endAdornment = endAdornment;
	}

	const inputLabelProps: Record<string, unknown> = {};
	if (inputLabelClassName) {
		inputLabelProps.className = inputLabelClassName;
	}
	if (inputLabelClasses) {
		inputLabelProps.classes = inputLabelClasses;
	}

	return {
		variant: "outlined", // was v3 inputVariant="outlined" — standard at every site.
		fullWidth,
		margin,
		required,
		error,
		helperText,
		placeholder,
		InputProps: inputProps,
		InputLabelProps: inputLabelProps,
		FormHelperTextProps: helperTextClassName
			? { className: helperTextClassName }
			: undefined,
	} as TextFieldProps;
};

const buildSlotProps = (props: CommonProps, defaultAriaLabel: string) => ({
	textField: buildTextFieldSlotProps(props),
	openPickerButton: {
		size: "small" as const,
		"aria-label": props.openPickerAriaLabel ?? defaultAriaLabel,
	},
	...(props.popperPlacement
		? { popper: { placement: props.popperPlacement } }
		: {}),
});

const buildSlots = (openPickerIcon?: React.ReactNode) =>
	openPickerIcon ? { openPickerIcon: () => <>{openPickerIcon}</> } : undefined;

export interface AppDatePickerProps extends CommonProps {
	views?: DateView[];
}

export const AppDatePicker: React.FC<AppDatePickerProps> = (props) => {
	const { value, onChange, label, format, disabled, minDate, maxDate, views } = props;
	return (
		<DatePicker
			value={value ?? null}
			onChange={onChange}
			label={label}
			format={format}
			disabled={disabled}
			minDate={minDate}
			maxDate={maxDate}
			views={views}
			slots={buildSlots(props.openPickerIcon)}
			slotProps={buildSlotProps(props, "change date") as any}
		/>
	);
};

export interface AppTimePickerProps extends CommonProps {
	views?: TimeView[];
	ampm?: boolean;
}

export const AppTimePicker: React.FC<AppTimePickerProps> = (props) => {
	const { value, onChange, label, format, disabled, views, ampm } = props;
	return (
		<TimePicker
			value={value ?? null}
			onChange={onChange}
			label={label}
			format={format}
			disabled={disabled}
			views={views}
			ampm={ampm}
			// Render the analog clock face in the desktop popper instead of v6's default
			// three-column digital clock — closer to the v3 picker users know, and keeps
			// field typing + auto-accept (unlike MobileTimePicker's read-only dialog).
			// AM/PM renders inside the clock (ampmInClock) since desktop has no toolbar.
			viewRenderers={{
				hours: renderTimeViewClock,
				minutes: renderTimeViewClock,
			}}
			ampmInClock
			slots={buildSlots(props.openPickerIcon)}
			slotProps={buildSlotProps(props, "change time") as any}
		/>
	);
};
